(() => {
  'use strict';

  const SUMMARY_ID = 'oni-aov-cost-summary';
  const DRAWER_SELECTOR = '#aov-cart-drawer, .AOV-CartDrawer';
  let refreshTimer = null;
  let lastSignature = '';

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `€${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const recurringFrequency = (item) => {
    const allocation = item?.selling_plan_allocation;
    if (!allocation) return null;

    const text = [
      allocation.selling_plan?.name,
      ...(allocation.selling_plan?.options || []).map((option) => option?.value),
      ...(item.options_with_values || []).map((option) => option?.value)
    ].filter(Boolean).join(' ').toLowerCase();

    if (/year|annual|12\s*month/.test(text)) return 'yearly';
    if (/month|monthly|30\s*day|4\s*week/.test(text)) return 'monthly';
    if (/week|weekly|7\s*day/.test(text)) return 'weekly';

    return 'recurring';
  };

  const recurringUnitPrice = (item) => {
    const allocation = item?.selling_plan_allocation;
    const cents = allocation?.price ?? allocation?.per_delivery_price ?? item.final_price ?? item.price ?? 0;
    return Number(cents) * (Number(item.quantity) || 1);
  };

  async function getCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to refresh cart');
    return response.json();
  }

  function createSummary() {
    const node = document.createElement('section');
    node.id = SUMMARY_ID;
    node.className = 'oni-aov-cost-summary';
    node.setAttribute('aria-live', 'polite');
    return node;
  }

  function findMount(drawer) {
    const footer = drawer.querySelector('.AOV-CartDrawer-Footer');
    if (footer) return { parent: footer, before: footer.firstElementChild };

    const checkout = drawer.querySelector('button[class*="Checkout"], a[href*="checkout"], button[name="checkout"], form[action*="checkout"]');
    if (checkout?.parentElement) return { parent: checkout.parentElement, before: checkout };

    return null;
  }

  function labelFor(frequency) {
    if (frequency === 'monthly') return ['Then monthly', '/mo'];
    if (frequency === 'yearly') return ['Renews yearly', '/yr'];
    if (frequency === 'weekly') return ['Then weekly', '/wk'];
    return ['Recurring after today', ''];
  }

  function render(cart) {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;

    const mount = findMount(drawer);
    if (!mount) return;

    const recurring = { monthly: 0, yearly: 0, weekly: 0, recurring: 0 };
    const recurringNames = { monthly: [], yearly: [], weekly: [], recurring: [] };

    for (const item of cart.items || []) {
      const frequency = recurringFrequency(item);
      if (!frequency) continue;
      recurring[frequency] += recurringUnitPrice(item);
      recurringNames[frequency].push(item.product_title || item.title || 'Subscription');
    }

    const signature = JSON.stringify({
      total: cart.total_price,
      count: cart.item_count,
      recurring,
      items: (cart.items || []).map((item) => [item.key, item.quantity, item.final_line_price])
    });

    let summary = drawer.querySelector(`#${SUMMARY_ID}`);
    if (!summary) {
      summary = createSummary();
      mount.parent.insertBefore(summary, mount.before || null);
    }

    if (signature === lastSignature && summary.dataset.ready === 'true') return;
    lastSignature = signature;

    const rows = Object.entries(recurring)
      .filter(([, cents]) => cents > 0)
      .map(([frequency, cents]) => {
        const [label, suffix] = labelFor(frequency);
        const names = [...new Set(recurringNames[frequency])].join(' + ');
        return `
          <div class="oni-cost-row oni-cost-row--recurring">
            <div class="oni-cost-copy">
              <strong>${label}</strong>
              <span>${names || 'Selected subscriptions'}</span>
            </div>
            <strong class="oni-cost-price">${money(cents, cart.currency)}${suffix}</strong>
          </div>`;
      }).join('');

    summary.innerHTML = `
      <div class="oni-cost-row oni-cost-row--today">
        <div class="oni-cost-copy">
          <strong>Due today</strong>
          <span>One-time items, setup fees and the first subscription payment.</span>
        </div>
        <strong class="oni-cost-price">${money(cart.total_price, cart.currency)}</strong>
      </div>
      ${rows}
    `;
    summary.dataset.ready = 'true';
  }

  async function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      try {
        render(await getCart());
      } catch (_) {
        // Keep AOV.ai's drawer fully functional if the explanatory panel cannot refresh.
      }
    }, 80);
  }

  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      const target = mutation.target?.nodeType === 1 ? mutation.target : mutation.target?.parentElement;
      return target?.closest?.(DRAWER_SELECTOR) || [...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.matches?.(DRAWER_SELECTOR) || node.querySelector?.(DRAWER_SELECTOR)));
    });
    if (relevant) refresh();
  });

  const patchNetwork = () => {
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      const response = await originalFetch.apply(this, args);
      const url = String(args[0]?.url || args[0] || '');
      if (/\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(url)) refresh();
      return response;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this.__oniCartUrl = String(url || '');
      return originalOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function(...args) {
      if (/\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(this.__oniCartUrl || '')) {
        this.addEventListener('loadend', refresh, { once: true });
      }
      return originalSend.apply(this, args);
    };
  };

  function init() {
    patchNetwork();
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('cart:updated', refresh);
    document.addEventListener('cart:refresh', refresh);
    document.addEventListener('click', (event) => {
      if (event.target.closest(`${DRAWER_SELECTOR} button, ${DRAWER_SELECTOR} a, [data-oni-cart-trigger]`)) refresh();
    }, true);
    refresh();
  }

  const start = () => {
    if (document.body?.dataset?.oniHome === 'true') {
      if ('requestIdleCallback' in window) requestIdleCallback(init, { timeout: 1200 });
      else setTimeout(init, 650);
    } else {
      init();
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
